import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type Customer = {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  gender: 'male' | 'female' | null;
  phone: string;
  additionalPhones: string[];
  email: string | null;
  address: string | null;
  registrationAddress: string | null;
  passportSeries: string | null;
  passportNumber: string | null;
  passportIssueDate: string | null;
  passportExpiryDate: string | null;
  passportIssuedBy: string | null;
  passportFileUrl: string | null;
  pinfl: string | null;
  dateOfBirth: string | null;
  source: string;
  sourceDetail: string | null;
  notes: string | null;
  status: 'active' | 'delinquent';
  cumulativeLateDays: number;
  delinquentAt: string | null;
  tags: string[];
  preferredLanguage: 'ru' | 'uz' | 'en';
  smsOptedOut: boolean;
  assignedAgentId: string | null;
  assignedAgent: { id: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
};

type ListResponse = {
  data: Customer[];
  pagination: { cursor: string | null; hasMore: boolean; limit: number; total: number };
};

export type CreateCustomerInput = {
  firstName: string;
  lastName: string;
  middleName: string;
  phone: string;
  additionalPhones?: string[];
  email?: string;
  address: string;
  registrationAddress?: string;
  dateOfBirth: string;
  gender?: 'male' | 'female';
  // Passport core fields — required server-side
  passportSeries: string;
  passportNumber: string;
  passportIssueDate: string;
  passportExpiryDate: string;
  passportIssuedBy: string;
  passportFileUrl?: string;
  pinfl: string;
  source?: string;
  notes?: string;
  tags?: string[];
  preferredLanguage?: 'ru' | 'uz' | 'en';
  assignedAgentId?: string;
};

export type UploadedFile = {
  key: string;
  viewUrl: string;
  size: number;
  mimeType: string;
  originalName: string;
};

export async function presignedDownload(key: string, ttl = 3600): Promise<string> {
  const res = await api.get<{ data: { url: string; ttl: number } }>('/uploads/presign', {
    params: { key, ttl },
  });
  return res.data.data.url;
}

export function useCustomersList(params?: { search?: string }) {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: async () => {
      const res = await api.get<ListResponse>('/customers', { params });
      return res.data;
    },
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ['customer', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: Customer }>(`/customers/${id}`);
      return res.data.data;
    },
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCustomerInput) => {
      const res = await api.post<{ data: Customer }>('/customers', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export type UpdateCustomerInput = {
  firstName?: string;
  lastName?: string;
  middleName?: string | null;
  dateOfBirth?: string | null;
  gender?: 'male' | 'female' | null;
  additionalPhones?: string[];
  email?: string | null;
  address?: string | null;
  registrationAddress?: string | null;
  passportSeries?: string | null;
  passportNumber?: string | null;
  passportIssueDate?: string | null;
  passportExpiryDate?: string | null;
  passportIssuedBy?: string | null;
  passportFileUrl?: string | null;
  pinfl?: string | null;
  source?: string;
  sourceDetail?: string | null;
  notes?: string | null;
  tags?: string[];
  preferredLanguage?: 'ru' | 'uz' | 'en';
  assignedAgentId?: string | null;
};

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateCustomerInput }) => {
      const res = await api.patch<{ data: Customer }>(`/customers/${id}`, input);
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['customer', data.id] });
    },
  });
}

export function useReactivateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ data: Customer }>(`/customers/${id}/reactivate`);
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['customer', data.id] });
      void qc.invalidateQueries({ queryKey: ['customer-balance', data.id] });
      void qc.invalidateQueries({ queryKey: ['customer-timeline', data.id] });
    },
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/customers/${id}`);
      return id;
    },
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['customer', id] });
    },
  });
}
